import type { TenantEntry } from '@linksoft/tenant-registry';
import type { CustomerConfigState } from '../../shared/types/customer';
import { toCustomerConfigState } from '../../shared/utils/tenant-config';
import { DEV_TENANT_COOKIE, readCookieValue } from '../../shared/utils/dev-tenant';

/**
 * The single tenant-override predicate for app-side code (risk 16). Before this
 * existed the `import.meta.dev` + override-cookie test was written out by hand
 * in seven places, in four mutually incompatible cookie dialects, on both sides
 * of the SSR boundary.
 *
 * Returns the overriding deployment key, or null when no override is in force.
 * `import.meta.dev` is a build-time constant, so a production bundle contains
 * neither the cookie read nor this branch.
 *
 * Server-only code must NOT call this: it uses `getCustomerFromEvent`, which
 * delegates to the package's `resolveTenantFromEvent`.
 */
export function getDevTenantOverride(): string | null {
	if (!import.meta.dev) return null;

	const header = import.meta.server
		? useRequestHeaders(['cookie']).cookie
		: globalThis.document?.cookie;

	return readCookieValue(header, DEV_TENANT_COOKIE);
}

/**
 * The tenant baked in at build time by the registry module's `appConfig.tenant`,
 * or null when there is none or a dev override is displacing it.
 *
 * Callers that get null fall back to per-request resolution. Keeping the
 * precedence in one place is the point: a caller that drifts here serves one
 * tenant's theme with another tenant's branding.
 */
export function resolveStaticCustomerConfig(): CustomerConfigState | null {
	if (getDevTenantOverride()) return null;

	const entry = useAppConfig().tenant as TenantEntry | undefined;
	return entry ? toCustomerConfigState(entry) : null;
}
